import React, { memo } from 'react';

export type SortField = 'company' | 'firstName' | 'lastName' | 'email' | 'source';
export type SortDirection = 'asc' | 'desc';

interface ColumnFiltersProps {
  showFilters: boolean;
  columnFilters: {
    company: string;
    lastName: string;
    firstName: string;
    email: string;
  };
  sortField: SortField | null;
  sortDirection: SortDirection;
  onFilterChange: (column: keyof ColumnFiltersProps['columnFilters'], value: string) => void;
  onSortChange: (field: SortField) => void;
  onClearFilters: () => void;
  onCloseFilters: () => void;
}

const ColumnFilters = memo<ColumnFiltersProps>(({ 
  showFilters, 
  columnFilters, 
  onFilterChange, 
  sortField,
  sortDirection,
  onSortChange,
  onClearFilters,
  onCloseFilters
}) => {
  if (!showFilters) return null;

  return (
    <div className="flex justify-center mb-2">
      <div className="relative w-full max-w-4xl bg-gray-50 border border-gray-200 rounded-lg p-4 pb-3">
        {/* Close button */}
        <button
          onClick={onCloseFilters}
          className="absolute top-2 right-3 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close filters"
        >
          ✕
        </button>
        
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Company
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="Filter company..."
                value={columnFilters.company}
                onChange={(e) => onFilterChange('company', e.target.value)}
                className="w-full px-3 py-2 pr-10 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                onClick={() => onSortChange('company')}
                className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-1 rounded transition-colors ${
                  sortField === 'company'
                    ? 'text-blue-600 hover:bg-blue-100'
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                }`}
                title={`Sort by Company ${sortField === 'company' ? `(${sortDirection === 'asc' ? 'Ascending' : 'Descending'})` : ''}`}
              >
                {sortField === 'company' ? (
                  sortDirection === 'asc' ? (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M5 8l5 5 5-5H5z" />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M5 12l5-5 5 5H5z" />
                    </svg>
                  )
                ) : (
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M5 12l5-5 5 5H5z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Last Name
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="Filter last name..."
                value={columnFilters.lastName}
                onChange={(e) => onFilterChange('lastName', e.target.value)}
                className="w-full px-3 py-2 pr-10 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                onClick={() => onSortChange('lastName')}
                className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-1 rounded transition-colors ${
                  sortField === 'lastName'
                    ? 'text-blue-600 hover:bg-blue-100'
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                }`}
                title={`Sort by Last Name ${sortField === 'lastName' ? `(${sortDirection === 'asc' ? 'Ascending' : 'Descending'})` : ''}`}
              >
                {sortField === 'lastName' ? (
                  sortDirection === 'asc' ? (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M5 8l5 5 5-5H5z" />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M5 12l5-5 5 5H5z" />
                    </svg>
                  )
                ) : (
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M5 12l5-5 5 5H5z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              First Name
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="Filter first name..."
                value={columnFilters.firstName}
                onChange={(e) => onFilterChange('firstName', e.target.value)}
                className="w-full px-3 py-2 pr-10 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                onClick={() => onSortChange('firstName')}
                className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-1 rounded transition-colors ${
                  sortField === 'firstName'
                    ? 'text-blue-600 hover:bg-blue-100'
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                }`}
                title={`Sort by First Name ${sortField === 'firstName' ? `(${sortDirection === 'asc' ? 'Ascending' : 'Descending'})` : ''}`}
              >
                {sortField === 'firstName' ? (
                  sortDirection === 'asc' ? (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M5 8l5 5 5-5H5z" />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M5 12l5-5 5 5H5z" />
                    </svg>
                  )
                ) : (
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M5 12l5-5 5 5H5z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="text"
              placeholder="Filter email..."
              value={columnFilters.email}
              onChange={(e) => onFilterChange('email', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        {/* Clear filters button */}
        {(columnFilters.company || columnFilters.lastName || columnFilters.firstName || columnFilters.email) && (
          <div className="mt-3 flex justify-end">
            <button
              onClick={onClearFilters}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

ColumnFilters.displayName = 'ColumnFilters';

export default ColumnFilters;
